import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { approveCampaignBudget } from '@features/campaigns/details/services/approveCampaignBudget';
import { QUERY_KEYS } from '@features/campaigns/shared/consts/queryKeys';
import { SuccessMessage } from '@shared/components/Toast/SuccessMessage/SuccessMessage';
import { i18nNameSpace } from '@shared/consts/i18n';
import { CampaignId } from '@shared/types/campaign';

interface UseApproveCampaignBudgetProps {
  campaignId: CampaignId;
  campaignName: string;
  handleToggleModalVisibility: () => void;
}

export function useApproveCampaignBudget({
  campaignId,
  campaignName,
  handleToggleModalVisibility,
}: UseApproveCampaignBudgetProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const queryClient = useQueryClient();

  const { mutate: approveCampaignBudgetMutation, isPending: approveCampaignBudgetIsPending } = useMutation({
    mutationFn: () => approveCampaignBudget(campaignId),
    onSuccess: async () => {
      toast.success(
        <SuccessMessage message={t('api.success.campaign.budgetApproved', { ns: i18nNameSpace.API, campaignName })} />,
      );
      handleToggleModalVisibility();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CAMPAIGN_DETAILS] });
    },
    onError: () => {
      handleToggleModalVisibility();
    },
  });

  const handleApproveBudgetConfirmation = () => {
    approveCampaignBudgetMutation();
  };

  return {
    approveCampaignBudgetIsPending,
    handleApproveBudgetConfirmation,
  };
}
